import * as React from "react";
import { animate, useMotionValue, useMotionValueEvent, useScroll, type MotionValue } from "motion/react";

const EASE = [0.16, 1, 0.3, 1] as const;

export interface HideOnScrollState {
    // The header's vertical offset as a CSS transform value (a % of the
    // header's own height, so hiding is exact even before `headerHeight` is
    // measured — see the comment below).
    y: MotionValue<string>;
    // Whether it's currently shown — drives aria-hidden/inert so the
    // off-screen copy isn't exposed as a duplicate landmark or focus target.
    visible: boolean;
}

const HIDDEN = "-100%";
const SHOWN = "0%";

/**
 * Drives a floating header's shown/hidden state from scroll. Hidden until
 * scroll passes `headerHeight`, then a directional "headroom" overlay: hides
 * on any downward scroll, reveals on any upward scroll — both animated.
 * `forceVisible` pins it open regardless of scroll (e.g. while its own
 * subnav/menu is open).
 *
 * `y` is expressed as a percentage (`-100%`/`0%`) rather than a pixel offset
 * derived from `headerHeight`, so the header is always fully off-screen when
 * hidden — even on the very first paint, before `headerHeight` has been
 * measured from the real DOM node (it starts from a hardcoded guess). A
 * pixel-based offset that undershoots the real height leaves a sliver of the
 * header (and its logo) visible until the guess is corrected.
 */
export function useHideOnScroll(headerHeight: number, forceVisible: boolean): HideOnScrollState {
    const { scrollY } = useScroll();
    const y = useMotionValue(HIDDEN);
    const [visible, setVisible] = React.useState(false);
    const lastY = React.useRef(0);

    useMotionValueEvent(scrollY, "change", (latest) => {
        const previous = lastY.current;
        lastY.current = latest;
        if (forceVisible) return;

        if (latest <= headerHeight) {
            y.stop();
            y.set(HIDDEN);
            setVisible(false);
            return;
        }

        if (latest > previous) {
            animate(y, HIDDEN, { duration: 0.5, ease: EASE });
            setVisible(false);
        } else if (latest < previous) {
            animate(y, SHOWN, { duration: 0.5, ease: EASE });
            setVisible(true);
        }
    });

    React.useEffect(() => {
        if (forceVisible) {
            animate(y, SHOWN, { duration: 0.3, ease: EASE });
        } else if (lastY.current <= headerHeight) {
            y.stop();
            y.set(HIDDEN);
            setVisible(false);
        }
    }, [forceVisible, headerHeight, y]);

    return { y, visible: forceVisible || visible };
}
